import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { UniversalPage, Card, NotificationCard, StatusBadge, type StatusTone } from '@sighthost/ui';
import {
  adminReportsService,
  type ProvisioningStats,
} from '@/domains/reports/services/adminReportsService';

const STATUS_TONE: Record<string, StatusTone> = {
  pending: 'pending',
  running: 'info',
  complete: 'active',
  failed_retryable: 'expiring',
  failed_terminal: 'failed',
  cancelled: 'draft',
};

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  return `${Math.round(ms / 86_400_000)}d ago`;
}

export default function ProvisioningHealthPage() {
  const [data, setData] = useState<ProvisioningStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminReportsService
      .getProvisioningStats()
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  // F-18. There was one success rate and it was lifetime — 79 jobs since
  // 2026-05-01, so a bad fortnight moves it by a point and nobody notices.
  // Measured on the day this shipped: lifetime 92.4%, last 30 days 86.8%. The
  // recent window is the one that tells you whether provisioning is healthy now.
  const lifetimeRate =
    data && data.total > 0 ? ((data.complete / data.total) * 100).toFixed(1) : null;
  const recentRate =
    data && data.window_30d.total > 0
      ? ((data.window_30d.complete / data.window_30d.total) * 100).toFixed(1)
      : null;

  return (
    <UniversalPage
      title="Provisioning health"
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Reports', href: '/admin/reports' },
        { label: 'Provisioning' },
      ]}
    >
      {error && <NotificationCard tone="danger" title="Could not load provisioning stats" body={error} />}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SmallStat
          label="Success rate · last 30d"
          value={recentRate ? `${recentRate}%` : data && data.window_30d.total === 0 ? 'no jobs' : '…'}
          tone={recentRate ? (Number(recentRate) >= 95 ? 'success' : 'warning') : 'muted'}
          hint={data ? `${data.window_30d.complete} of ${data.window_30d.total} jobs` : undefined}
        />
        <SmallStat
          label="Success rate · lifetime"
          value={lifetimeRate ? `${lifetimeRate}%` : '…'}
          hint={data ? `${data.complete} of ${data.total} since first job` : undefined}
        />
        <SmallStat label="Active (pending + running)" value={data ? data.pending + data.running : undefined} />
        <SmallStat
          label="Failed (terminal + retryable)"
          value={data ? data.failed_terminal + data.failed_retryable : undefined}
          tone={data && data.failed_terminal > 0 ? 'danger' : 'muted'}
          hint={data && data.failed_terminal > 0 ? `${data.failed_terminal} terminal — nothing retries these` : undefined}
        />
      </div>

      <Card interactive={false}>
        <header className="border-b border-border p-4">
          <h3 className="text-sm font-semibold text-foreground">By status</h3>
        </header>
        <div className="grid grid-cols-2 gap-2 p-4 sm:grid-cols-3 lg:grid-cols-6">
          {[
            ['pending', data?.pending],
            ['running', data?.running],
            ['complete', data?.complete],
            ['failed_retryable', data?.failed_retryable],
            ['failed_terminal', data?.failed_terminal],
            ['cancelled', data?.cancelled],
          ].map(([status, n]) => (
            <div key={status as string} className="rounded border border-border p-3">
              <StatusBadge tone={STATUS_TONE[status as string] ?? 'draft'}>{status}</StatusBadge>
              <p className="mt-2 text-2xl font-semibold text-foreground">{n ?? '…'}</p>
            </div>
          ))}
        </div>
      </Card>

      <Card interactive={false}>
        <header className="border-b border-border p-4">
          <h3 className="text-sm font-semibold text-foreground">By step kind</h3>
        </header>
        <div className="divide-y divide-border">
          {(data?.by_step_kind ?? []).map((row) => (
            <div key={row.step_kind} className="flex items-center justify-between p-3 text-sm">
              <code className="text-xs text-foreground">{row.step_kind}</code>
              <span className="text-muted-foreground">{row.count}</span>
            </div>
          ))}
          {data?.by_step_kind.length === 0 && (
            <p className="p-6 text-center text-sm text-muted-foreground">No jobs yet.</p>
          )}
        </div>
      </Card>

      <Card interactive={false}>
        <header className="flex items-center justify-between border-b border-border p-4">
          <h3 className="text-sm font-semibold text-foreground">Recent failures (last 20)</h3>
          <Link to="/admin/ops/provisioning" className="text-xs font-medium text-primary-ink hover:underline">
            Open queue →
          </Link>
        </header>
        {(data?.recent_failures ?? []).length === 0 ? (
          <p className="p-8 text-center text-sm text-muted-foreground">
            No failures — provisioning is healthy.
          </p>
        ) : (
          <div className="divide-y divide-border">
            {(data?.recent_failures ?? []).map((f) => (
              <div key={f.id} className="space-y-1 p-3 text-sm">
                <div className="flex items-center justify-between">
                  <code className="text-xs text-foreground">{f.step_kind}</code>
                  <span className="text-xs text-muted-foreground">{formatRelative(f.finished_at)}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Target: <code>{f.target_ref ?? '—'}</code> · attempts {f.attempt_count}
                </p>
                {f.error_message && (
                  <p className="rounded bg-danger/5 p-2 text-xs text-danger">{f.error_message}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </UniversalPage>
  );
}

function SmallStat({
  label,
  value,
  tone = 'muted',
  hint,
}: {
  label: string;
  value: string | number | undefined;
  tone?: 'success' | 'warning' | 'danger' | 'muted';
  /** The denominator, in words. A percentage over 6 jobs and one over 600 read
   *  identically without it. */
  hint?: string;
}) {
  const valueClass =
    tone === 'success'
      ? 'text-success'
      : tone === 'warning'
        ? 'text-warning-dark'
        : tone === 'danger'
          ? 'text-danger'
          : 'text-foreground';
  return (
    <Card interactive={false} className="p-4">
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-2 text-2xl font-semibold ${valueClass}`}>{value ?? '…'}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </Card>
  );
}
